import { type CreditPack, SOCIAL_IMAGE_CREDIT_COST } from '@pulsify/shared/dto'
import { motion, useReducedMotion, useSpring, useTransform } from 'framer-motion'
import { Coins, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { creditPackPrice, creditPackStartCredits } from '~/entities/credits/lib/credit-pack-amount'
import {
  type CreditTopUpRequest,
  onCreditTopUpRequest,
} from '~/entities/credits/model/credit-top-up'
import { useBuyCreditPack, useCreditPack } from '~/entities/credits/model/use-credit-pack'
import { formatCredits } from '~/entities/subscription/plans'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { SPRING } from '~/shared/lib/motion'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Slider } from '~/shared/ui/slider'

const QUICK_PICKS = [5_000, 10_000, 25_000, 50_000]

/** Opens the credit top-up whenever any part of the dashboard asks for it. */
export function CreditTopUpHost() {
  const { data: business } = useCurrentBusiness()
  const pack = useCreditPack()
  const [request, setRequest] = useState<CreditTopUpRequest | null>(null)

  useEffect(() => onCreditTopUpRequest(setRequest), [])

  if (!business?.id || !pack) return null
  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && setRequest(null)}>
      <DialogContent className="sm:max-w-md">
        {request && (
          <CreditTopUp businessId={business.id} pack={pack} shortfall={request.shortfall} />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface CreditTopUpProps {
  businessId: string
  pack: CreditPack
  shortfall?: number
}

function CreditTopUp({ businessId, pack, shortfall }: CreditTopUpProps) {
  const [credits, setCredits] = useState(() => creditPackStartCredits(pack, shortfall))
  const { mutate, isPending, isSuccess } = useBuyCreditPack(businessId)
  const redirecting = isPending || isSuccess
  const price = formatMoney(creditPackPrice(pack, credits), pack.currency, getLocale())
  const quickPicks = QUICK_PICKS.filter(
    (pick) => pick >= pack.minCredits && pick <= pack.maxCredits,
  )

  return (
    <div className="space-y-6">
      <DialogHeader className="items-center text-center sm:text-center">
        <span className="mb-1 grid size-11 place-items-center rounded-full bg-brand-subtle text-brand-subtle-foreground">
          <Coins className="size-5" />
        </span>
        <DialogTitle>{m.creditPackTitle()}</DialogTitle>
        <DialogDescription>
          {shortfall && shortfall > 0
            ? m.creditTopUpShortfall({ credits: formatCredits(shortfall) })
            : m.creditTopUpDescription()}
        </DialogDescription>
      </DialogHeader>

      <div className="text-center">
        <p className="font-semibold font-serif text-5xl text-foreground tabular-nums tracking-tight">
          <AnimatedCredits value={credits} />
        </p>
        <p className="mt-1 text-muted-foreground text-sm">{m.creditsUnit()}</p>
        <p className="mt-3 flex items-baseline justify-center gap-1.5">
          <span className="font-semibold text-foreground text-xl tabular-nums">{price}</span>
          <span className="text-muted-foreground text-xs">{m.creditPackNet()}</span>
        </p>
        <p className="mt-1 text-muted-foreground text-xs">
          {m.creditTopUpImages({
            count: formatCredits(Math.floor(credits / SOCIAL_IMAGE_CREDIT_COST)),
          })}
        </p>
      </div>

      <div className="space-y-1">
        <Slider
          size="lg"
          min={pack.minCredits}
          max={pack.maxCredits}
          step={pack.stepCredits}
          value={[credits]}
          onValueChange={([value]) => {
            if (value !== undefined) setCredits(value)
          }}
          disabled={redirecting}
          thumbLabel={m.creditPackAmountLabel()}
          thumbValueText={m.creditPackAmount({ credits: formatCredits(credits), price })}
        />
        <div className="grid grid-cols-4 gap-2 pt-1">
          {quickPicks.map((pick) => (
            <button
              key={pick}
              type="button"
              disabled={redirecting}
              aria-pressed={credits === pick}
              onClick={() => setCredits(pick)}
              className={cn(
                'rounded-full border px-2 py-1.5 text-xs tabular-nums transition-colors motion-reduce:transition-none',
                credits === pick
                  ? 'border-brand bg-brand-subtle font-medium text-brand-subtle-foreground'
                  : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
              )}
            >
              {formatCredits(pick)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Button
          type="button"
          className="w-full"
          size="lg"
          disabled={redirecting}
          onClick={() => mutate(credits)}
        >
          {redirecting ? <Loader2 className="animate-spin" /> : <Coins />}
          {m.creditTopUpPay({ price })}
        </Button>
        <p className="text-center text-muted-foreground text-xs">{m.creditTopUpKeep()}</p>
      </div>
    </div>
  )
}

function AnimatedCredits({ value }: { value: number }) {
  const reduceMotion = useReducedMotion()
  const spring = useSpring(value, { stiffness: SPRING.stiffness, damping: SPRING.damping })
  const text = useTransform(spring, (current) => formatCredits(Math.round(current / 100) * 100))

  useEffect(() => {
    spring.set(value)
  }, [spring, value])

  if (reduceMotion) return <span>{formatCredits(value)}</span>
  return <motion.span>{text}</motion.span>
}
